/**
 * useCatalog Hook
 *
 * React Query hooks for supplier catalog items.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import {
  catalogService,
  type QueryCatalogItemsDto,
  type CreateCatalogItemDto,
  type UpdateCatalogItemDto,
  type UpdateCatalogItemPriceDto,
} from '../services/suppliers.service';
import { supplierKeys } from './useSuppliers';

/**
 * Query key factory for catalog items
 */
export const catalogKeys = {
  all: ['catalog'] as const,
  lists: () => [...catalogKeys.all, 'list'] as const,
  list: (supplierId: string, params: QueryCatalogItemsDto) =>
    [...catalogKeys.lists(), supplierId, params] as const,
  details: () => [...catalogKeys.all, 'detail'] as const,
  detail: (supplierId: string, itemId: string) =>
    [...catalogKeys.details(), supplierId, itemId] as const,
  priceHistory: (supplierId: string, itemId: string) =>
    [...catalogKeys.all, 'priceHistory', supplierId, itemId] as const,
  purchasePrices: (rawProductId: string) =>
    [...catalogKeys.all, 'purchasePrices', rawProductId] as const,
};

/**
 * Hook to fetch catalog items for a supplier
 */
export function useCatalogItems(supplierId: string | null, params: QueryCatalogItemsDto) {
  return useQuery({
    queryKey: catalogKeys.list(supplierId!, params),
    queryFn: () => catalogService.list(supplierId!, params),
    enabled: !!supplierId,
    placeholderData: (prev) => prev,
  });
}

/**
 * Hook to fetch single catalog item
 */
export function useCatalogItem(supplierId: string | null, itemId: string | null) {
  return useQuery({
    queryKey: catalogKeys.detail(supplierId!, itemId!),
    queryFn: () => catalogService.getById(supplierId!, itemId!),
    enabled: !!supplierId && !!itemId,
  });
}

/**
 * Hook to fetch price history for a catalog item
 */
export function usePriceHistory(supplierId: string | null, itemId: string | null) {
  return useQuery({
    queryKey: catalogKeys.priceHistory(supplierId!, itemId!),
    queryFn: () => catalogService.getPriceHistory(supplierId!, itemId!),
    enabled: !!supplierId && !!itemId,
  });
}

/**
 * Hook to fetch purchase prices for a raw product
 */
export function usePurchasePricesForProduct(rawProductId: string | null) {
  return useQuery({
    queryKey: catalogKeys.purchasePrices(rawProductId!),
    queryFn: () => catalogService.getPurchasePricesForProduct(rawProductId!),
    enabled: !!rawProductId,
  });
}

/**
 * Hook to create a catalog item
 */
export function useCreateCatalogItem(supplierId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateCatalogItemDto) => catalogService.create(supplierId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: catalogKeys.lists() });
      queryClient.invalidateQueries({ queryKey: supplierKeys.all });
      toast.success('Reference ajoutee avec succes');
    },
    onError: (error: Error & { response?: { data?: { error?: { message?: string } } } }) => {
      toast.error(error.response?.data?.error?.message ?? 'Erreur lors de la creation');
    },
  });
}

/**
 * Hook to update a catalog item
 */
export function useUpdateCatalogItem(supplierId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ itemId, data }: { itemId: string; data: UpdateCatalogItemDto }) =>
      catalogService.update(supplierId, itemId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: catalogKeys.lists() });
      toast.success('Reference mise a jour');
    },
    onError: (error: Error & { response?: { data?: { error?: { message?: string } } } }) => {
      toast.error(error.response?.data?.error?.message ?? 'Erreur lors de la mise a jour');
    },
  });
}

/**
 * Hook to update catalog item price
 */
export function useUpdateCatalogItemPrice(supplierId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ itemId, data }: { itemId: string; data: UpdateCatalogItemPriceDto }) =>
      catalogService.updatePrice(supplierId, itemId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: catalogKeys.lists() });
      queryClient.invalidateQueries({ queryKey: catalogKeys.details() });
      toast.success('Prix mis a jour');
    },
    onError: (error: Error & { response?: { data?: { error?: { message?: string } } } }) => {
      toast.error(error.response?.data?.error?.message ?? 'Erreur lors de la mise a jour du prix');
    },
  });
}

/**
 * Hook to deactivate (soft delete) a catalog item
 */
export function useDeleteCatalogItem(supplierId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (itemId: string) => catalogService.deactivate(supplierId, itemId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: catalogKeys.lists() });
      queryClient.invalidateQueries({ queryKey: supplierKeys.all });
      toast.success('Reference supprimee');
    },
    onError: (error: Error & { response?: { data?: { error?: { message?: string } } } }) => {
      toast.error(error.response?.data?.error?.message ?? 'Erreur lors de la suppression');
    },
  });
}
